import type {
  ConditionGroup,
  Contact,
  DataTableEntityType,
  FamilyMember,
  FamilyMemberEdge,
  Household,
  HouseholdUpdatePayload,
  NextPageParams,
  PaginationMetadata,
  PersonDataEnrichment,
  SuggestedField,
  ViewColumn,
} from "@/schema"

import { ApiContext, apiContextBlobFetch, apiContextFetch, ApiRequestError } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export interface HouseholdsResponse {
  households: Household[]
  pagination: PaginationMetadata
}

export function fetchHouseholds(
  context: ApiContext,
  searchTerm?: string,
  householdType?: "Prospect" | "Client" | "prospect" | "client" | null,
  pageParams?: NextPageParams,
  minimal = true,
  scope?: "company"
) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (householdType) {
    // Capitalize for STI type matching (backend expects "Prospect" or "Client")
    const capitalizedType = householdType.charAt(0).toUpperCase() + householdType.slice(1).toLowerCase()
    params.append("type", capitalizedType)
  }

  if (pageParams) {
    params.append("page", String(pageParams.page))
    params.append("per_page", String(pageParams.per_page))
  }

  if (minimal) {
    params.append("minimal", "true")
  }

  if (scope) params.append("scope", scope)

  return apiContextFetch<HouseholdsResponse>(context, `households?${params.toString()}`)
}

export async function fetchHousehold(context: ApiContext, id: string): Promise<Household> {
  return await apiContextFetch<Household>(context, `households/${id}`)
}

export interface HouseholdTypeResponse {
  id: string
  type: "Client" | "Prospect" | "PastClient"
}

export async function fetchHouseholdType(context: ApiContext, id: string): Promise<HouseholdTypeResponse> {
  return await apiContextFetch<HouseholdTypeResponse>(context, `households/${id}/type`)
}

export async function updateHousehold(context: ApiContext, id: string, params: HouseholdUpdatePayload) {
  // Check if params contains a File object (profile_picture)
  const hasFile = Object.values(params).some((value) => value instanceof File)

  if (hasFile) {
    // Use FormData for file uploads
    const formData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(`household[${key}]`, value as string | Blob)
      }
    })

    return await apiContextFetch<Household>(context, `households/${id}`, {
      method: "PUT",
      body: formData,
      headers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "Content-Type": undefined as any,
      },
    })
  }

  // Default JSON behavior
  return await apiContextFetch<Household>(context, `households/${id}`, {
    method: "PUT",
    body: JSON.stringify({ household: params }),
  })
}

export async function promoteToClient(context: ApiContext, prospectId: string) {
  return await apiContextFetch<Household>(context, `prospects/${prospectId}/promote_to_client`, {
    method: "POST",
  })
}

export async function convertToContact(context: ApiContext, householdId: string): Promise<Contact> {
  return await apiContextFetch<Contact>(context, `households/${householdId}/convert_to_contact`, {
    method: "POST",
  })
}

export interface BulkConvertToContactsResponse {
  successful: unknown[]
  failed: unknown[]
}

export async function bulkConvertToContacts(
  context: ApiContext,
  householdIds: string[]
): Promise<BulkConvertToContactsResponse> {
  return await apiContextFetch<BulkConvertToContactsResponse>(context, `households/bulk_convert_to_contacts`, {
    method: "POST",
    body: JSON.stringify({ household_ids: householdIds }),
  })
}

export function destroyHousehold(context: ApiContext, id: string) {
  return apiContextFetch<Household>(context, `households/${id}`, {
    method: "DELETE",
  })
}

export interface RecentHouseholdsResponse {
  households: Array<{
    id: string
    name: string
    type: "Client" | "Prospect" | "PastClient"
    tier: number | null
    status: string
    viewed_at: string | null
    street_address?: string | null
    zip?: string | null
    ai_summary?: string | null
    tags?: string[]
    tag_count?: number
    profile_picture_url?: string | null
    book_id?: string
    book_name?: string
    book_initials?: string
    head_of_household?: {
      first_name: string
      last_name: string
    }
    members?: Array<{
      name: string
      role?: string
      email?: string
      phone?: string
      phone_extension?: string | null
    }>
    city?: string
    state?: string
  }>
}

export function fetchRecentHouseholds(context: ApiContext) {
  return apiContextFetch<RecentHouseholdsResponse>(context, `households/recent_views`)
}

export interface HouseholdSuggestedFieldsResponse {
  suggested_fields: SuggestedField[]
}

export interface HouseholdSuggestedFieldResponse {
  suggested_field: SuggestedField
}

export function fetchHouseholdSuggestedFields(context: ApiContext, householdId: string) {
  return apiContextFetch<HouseholdSuggestedFieldsResponse>(context, `households/${householdId}/suggested_fields`)
}

export function applySuggestedField(context: ApiContext, householdId: string, suggestedFieldId: string) {
  return apiContextFetch<HouseholdSuggestedFieldResponse>(
    context,
    `households/${householdId}/suggested_fields/${suggestedFieldId}/apply`,
    {
      method: "POST",
    }
  )
}

export function dismissSuggestedField(context: ApiContext, householdId: string, suggestedFieldId: string) {
  return apiContextFetch<HouseholdSuggestedFieldResponse>(
    context,
    `households/${householdId}/suggested_fields/${suggestedFieldId}/dismiss`,
    {
      method: "POST",
    }
  )
}

export interface HouseholdPersonDataEnrichmentResponse {
  person_data_enrichment: PersonDataEnrichment | null
}

export async function fetchHouseholdPersonDataEnrichment(
  context: ApiContext,
  householdId: string
): Promise<HouseholdPersonDataEnrichmentResponse> {
  try {
    return await apiContextFetch<HouseholdPersonDataEnrichmentResponse>(
      context,
      `households/${householdId}/person_data_enrichment`
    )
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) {
      return { person_data_enrichment: null }
    }
    throw error
  }
}

export async function rejectHouseholdEnrichment(context: ApiContext, householdId: string) {
  return await apiContextFetch<HouseholdPersonDataEnrichmentResponse>(
    context,
    `households/${householdId}/person_data_enrichment/reject`,
    {
      method: "POST",
    }
  )
}
export interface MergeHouseholdResponse {
  id: string
  household_type: "Client" | "Prospect" | "PastClient"
}

export interface MergeHouseholdParams {
  target_household_id: string
  person_roles?: Record<string, string>
  people_to_delete?: string[]
  person_merge_targets?: Record<string, string>
}

export async function mergeHousehold(
  context: ApiContext,
  id: string,
  params: MergeHouseholdParams
): Promise<MergeHouseholdResponse> {
  return await apiContextFetch<MergeHouseholdResponse>(context, `households/${id}/merge`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface HouseholdEnrichmentResponse {
  person_data_enrichment: PersonDataEnrichment
}

export async function enrichHousehold(context: ApiContext, id: string): Promise<HouseholdEnrichmentResponse> {
  return await apiContextFetch<HouseholdEnrichmentResponse>(context, `households/${id}/enrichment`, {
    method: "POST",
  })
}

export interface HouseholdsCsvExportParams {
  entityType: DataTableEntityType
  viewColumns: ViewColumn[]
  conditionGroup: ConditionGroup
  viewSort?: { field?: string; direction?: "asc" | "desc" }
  // In all-books mode the table shows a forced-visible Book column that is not part of the
  // persisted DataView, so inject it into the export to mirror what the user sees.
  includeBookColumn?: boolean
}

// Mirror the table: the forced-visible Book column sits right after Name, so insert it there
// (not appended) and renumber sort_order so the backend renders columns in the same order.
function withBookColumn(viewColumns: ViewColumn[], includeBookColumn?: boolean): ViewColumn[] {
  if (!includeBookColumn) return viewColumns

  const withoutBook = viewColumns
    .filter((vc) => vc.field !== "book.name" && vc.field !== "book_name")
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  const bookColumn: ViewColumn = { field: "book.name", name: "Book", is_visible: true, sort_order: 0 }
  const ordered = withoutBook.length > 0 ? [withoutBook[0], bookColumn, ...withoutBook.slice(1)] : [bookColumn]
  return ordered.map((vc, index) => ({ ...vc, sort_order: index }))
}

export async function downloadHouseholdsCSV(context: ApiContext, params: HouseholdsCsvExportParams): Promise<Blob> {
  const searchParams = new URLSearchParams()
  searchParams.append("entity_type", params.entityType)

  withBookColumn(params.viewColumns, params.includeBookColumn).forEach((vc, index) => {
    const prefix = `view_columns_attributes[${index}]`
    searchParams.append(`${prefix}[field]`, vc.field)
    if (vc.name) searchParams.append(`${prefix}[name]`, vc.name)
    searchParams.append(`${prefix}[is_visible]`, String(vc.is_visible))
    searchParams.append(`${prefix}[sort_order]`, String(vc.sort_order))
  })

  appendConditionGroupParams(searchParams, params.conditionGroup)
  appendViewSortParams(searchParams, params.viewSort)

  return apiContextBlobFetch(context, `households/csv_export?${searchParams.toString()}`)
}

export interface BulkMoveHouseholdsToBookParams {
  household_ids: string[]
  destination_book_id: string
}

export async function bulkMoveHouseholdsToBook(
  context: ApiContext,
  params: BulkMoveHouseholdsToBookParams
): Promise<void> {
  await apiContextFetch<void>(context, `household_book_moves`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface FamilyMembersResponse {
  family_members: FamilyMember[]
  edges: FamilyMemberEdge[]
  root_member_id: string | null
}

export async function fetchFamilyMembers(context: ApiContext, householdId: string): Promise<FamilyMembersResponse> {
  return await apiContextFetch<FamilyMembersResponse>(context, `households/${householdId}/family_members`)
}

export async function deleteFamilyMember(context: ApiContext, householdId: string, memberId: string): Promise<void> {
  await apiContextFetch<void>(context, `households/${householdId}/family_members/${memberId}`, {
    method: "DELETE",
  })
}

export async function createFamilyMemberFeedback(
  context: ApiContext,
  householdId: string,
  memberId: string,
  params: { sentiment: "positive" | "negative"; feedback_text?: string }
): Promise<void> {
  await apiContextFetch<void>(context, `households/${householdId}/family_members/${memberId}/feedbacks`, {
    method: "POST",
    body: JSON.stringify({ family_member_feedback: params }),
  })
}
